import type { Placement } from "@floating-ui/dom";

import { useAnchorPosition, usePopover } from "../interaction";
import { cn } from "../lib/cn";

export type { Placement } from "@floating-ui/dom";

// allow-discrete keeps a closing popover in the top layer until the fade ends.
const popoverMotion = cn(
  "transition-[opacity,translate,overlay,display] transition-discrete duration-150 ease-paper",
  "opacity-0 translate-y-1",
  "open:opacity-100 open:translate-y-0",
  "starting:open:opacity-0 starting:open:translate-y-1",
  "motion-reduce:transition-none",
);

export interface PopoverProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "role" | "id" | "className" | "children" | "popover" | "onToggle"
> {
  open: boolean;
  /** Fires for platform-driven closes too: light dismiss, Escape, another auto popover opening. */
  onOpenChange: (open: boolean) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  placement?: Placement;
  matchWidth?: boolean;
  role?: string;
  children: React.ReactNode;
  className?: string;
  id?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/** Top-layer Popover API surface, no portal. Children stay mounted while closed so ids resolve. */
export function Popover({
  open,
  onOpenChange,
  anchorRef,
  placement = "bottom-start",
  matchWidth = false,
  role,
  children,
  className,
  id,
  ref,
  ...rest
}: PopoverProps) {
  const { popoverRef, popoverProps } = usePopover({ open, onOpenChange });
  useAnchorPosition(anchorRef, popoverRef, {
    placement,
    offset: 6,
    matchWidth,
    enabled: open,
  });

  return (
    <div
      {...rest}
      id={id}
      role={role}
      ref={(node) => {
        popoverRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn(
        // whitespace-normal/text-left: the top layer still inherits text properties from the anchor's host.
        "rounded-paper border border-rule bg-card p-1 font-body whitespace-normal text-left shadow-lift-lg",
        popoverMotion,
        className,
      )}
      {...popoverProps}
    >
      {children}
    </div>
  );
}
